/**
 * User-flow instrumentation.
 *
 * Every meaningful user action (claim a spot, create/edit an event, roster
 * changes, outreach sends, admin actions) drops a Sentry breadcrumb and mints a
 * correlation id. The same correlation id is sent to the server on the next
 * server-function call (header) and written into the analytics/audit row, so a
 * Sentry issue can be traced back to the exact database record.
 */
import { addClientBreadcrumb, setSentryTags } from "./sentry-client";

export const CORRELATION_HEADER = "x-correlation-id";

let currentCorrelationId: string | null = null;

function randomId() {
  try {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 24);
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

/** Mints a fresh correlation id and makes it the active one for this action. */
export function newCorrelationId(): string {
  currentCorrelationId = randomId();
  return currentCorrelationId;
}

/** The correlation id for the action in flight (mints one if none is active). */
export function correlationId(): string {
  return currentCorrelationId ?? newCorrelationId();
}

export type FlowContext = {
  eventId?: string | null;
  signupId?: string | null;
  slotId?: string | null;
  contactId?: string | null;
  threadId?: string | null;
  [key: string]: unknown;
};

/**
 * Records a user-flow step. Returns the correlation id so callers can pass it
 * through to the server (analytics metadata, audit rows).
 */
export function flow(action: string, context: FlowContext = {}): string {
  const id = newCorrelationId();
  void setSentryTags({
    correlation_id: id,
    flow_action: action,
    event_id: context.eventId ?? undefined,
    signup_id: context.signupId ?? undefined,
  });
  void addClientBreadcrumb(action, { ...context, correlation_id: id }, "user-flow");
  return id;
}

/** Marks the outcome of a flow step without minting a new correlation id. */
export function flowResult(action: string, ok: boolean, context: FlowContext = {}) {
  void addClientBreadcrumb(
    `${action}:${ok ? "ok" : "failed"}`,
    { ...context, correlation_id: currentCorrelationId ?? undefined },
    "user-flow",
  );
}
